import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import {
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';

import { color, r, radius } from '../../theme/tokens';
import StackProductCard from '../products/StackProductCard';

/**
 * The inline products carousel that opens under a supplement row.
 *
 * Stack detail has shown products this way since phase 1; the dashboard's
 * recommended cards used to navigate to the supplement page instead, which
 * took the user off the dashboard to reach the buying path §3 puts on the card
 * itself. Tanu ruled on 2026-08-27 that the two should behave the same.
 *
 * This owns its own fetch and carousel position, so a caller only has to say
 * which supplement is open. `SupplementsTable` still has its own copy of this
 * markup inline — converging the two is a follow-up, deliberately not done
 * here because stack detail is out of this ticket's scope and has no test
 * suite to catch a regression.
 */
export default function SupplementProductsPanel({
  supplementId,
  supplementName,
  fetchProducts,
}) {
  const [products, setProducts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    (async () => {
      try {
        const found = await fetchProducts(supplementId);
        if (isMounted) setProducts(Array.isArray(found) ? found : []);
      } catch (error) {
        // A failed product lookup is not a card failure — the panel says so
        // and the rest of the stack keeps rendering.
        if (isMounted) setProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [supplementId, fetchProducts]);

  const total = products?.length || 0;
  const arrow = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    width: '2.75rem', // §8's hit target, same as everything else on this card
    height: '2.75rem',
    backgroundColor: color.paper,
    border: `${r(1)} solid ${color.line}`,
    color: color.ink,
    '&:hover': { backgroundColor: color.surface },
    '&:focus-visible': {
      outline: `${r(2)} solid ${color.accent}`,
      outlineOffset: r(2),
    },
  };

  return (
    <Box
      data-testid="supplement-products-panel"
      sx={{
        backgroundColor: color.surface,
        borderTop: `${r(1)} solid ${color.lineSoft}`,
        padding: `${r(12)} ${r(18)}`,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingBlock: '1rem' }}>
          <CircularProgress size="1.5rem" />
        </Box>
      ) : total > 0 ? (
        <Box sx={{ position: 'relative', height: '8.75rem' }}>
          {step > 0 && (
            <IconButton
              onClick={() => setStep((n) => n - 1)}
              aria-label="Previous product"
              sx={{ ...arrow, left: r(-8) }}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
          )}

          {step < total - 1 && (
            <IconButton
              onClick={() => setStep((n) => n + 1)}
              aria-label="Next product"
              sx={{ ...arrow, right: r(-8) }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          )}

          {total > 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: r(-6),
                right: r(8),
                zIndex: 1,
                paddingInline: '0.5rem',
                borderRadius: radius.pill,
                backgroundColor: color.paper,
                border: `${r(1)} solid ${color.line}`,
                fontSize: '0.6875rem',
                fontWeight: 700,
                color: color.inkSoft,
                lineHeight: '1.25rem',
              }}
            >
              {step + 1}/{total}
            </Box>
          )}

          <Box sx={{ height: '100%', width: '100%' }}>
            <StackProductCard
              product={{
                ...products[step],
                sourceSupplement: supplementName,
                supplementKey: supplementId,
              }}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: r(100),
            backgroundColor: color.paper,
            borderRadius: radius.sm,
            border: `${r(1)} dashed ${color.line}`,
          }}
        >
          <Typography variant="body2" sx={{ color: color.inkSoft, fontWeight: 500 }}>
            No products found for {supplementName}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
